import { Autocomplete, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import * as React from "react";

import { RobotoSwitch } from "@/shared/components/RobotoSwitch";
import {
  CustomFieldRecord,
  CustomFieldType,
} from "@/shared/domain/customFields";

import { guessTimezone, timestampToWireValue } from "./customFieldValueFormat";
import styles from "./CustomFieldValues.module.css";

export interface CustomFieldValueInputProps {
  field: CustomFieldRecord;
  value: unknown;
  onChange: (value: unknown) => void;
  /** Invoked when the user signals commit from inside the input (e.g. Enter). */
  onCommit?: () => void;
  autoFocus?: boolean;
}

/**
 * Renders the type-appropriate control for entering one custom-field value and
 * emits it in wire shape: string (String), number (Number), boolean (Boolean),
 * the chosen option string (Enum), or an ISO string (Timestamp). The caller owns
 * commit via an explicit confirm button; the text-entry controls also forward
 * Enter to `onCommit`, whereas Enum's Enter selects the highlighted option.
 */
export function CustomFieldValueInput({
  field,
  value,
  onChange,
  onCommit,
  autoFocus,
}: CustomFieldValueInputProps) {
  const commitOnEnter: React.KeyboardEventHandler = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onCommit?.();
    }
  };

  switch (field.field_type) {
    case CustomFieldType.Number:
      return (
        <NumberValueInput
          value={value}
          onChange={onChange}
          onCommit={onCommit}
          autoFocus={autoFocus}
        />
      );

    case CustomFieldType.Boolean:
      return (
        <RobotoSwitch
          className={styles.booleanSwitch}
          color="success"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
      );

    case CustomFieldType.Enum: {
      const options = field.options?.enum_values ?? [];
      return (
        <Autocomplete
          options={options}
          value={typeof value === "string" && value !== "" ? value : null}
          onChange={(_event, next) => onChange(next ?? null)}
          autoHighlight
          openOnFocus
          fullWidth
          size="small"
          // Keep the dropdown in the DOM subtree of whatever modal hosts us
          // (create popover/panel) so its outside-click detection doesn't treat
          // an option click as a dismiss; `strategy: "fixed"` then lets the
          // popper escape a host panel's `overflow: hidden` instead of clipping.
          slotProps={{
            popper: {
              disablePortal: true,
              popperOptions: { strategy: "fixed" },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus={autoFocus}
              placeholder="Select an option…"
            />
          )}
        />
      );
    }

    case CustomFieldType.Timestamp: {
      const timezone = guessTimezone();
      return (
        <DateTimePicker
          className={styles.timestampInput}
          value={
            typeof value === "string" || typeof value === "number"
              ? dayjs(value)
              : null
          }
          onChange={(next) => onChange(timestampToWireValue(next))}
          timezone={timezone}
          label={timezone}
          slotProps={{
            textField: { size: "small", fullWidth: true, autoFocus },
            // See the Enum Autocomplete above: keep the picker popper in-DOM so a
            // host modal doesn't dismiss on click, and fixed so it isn't clipped.
            popper: {
              disablePortal: true,
              popperOptions: { strategy: "fixed" },
            },
          }}
        />
      );
    }

    case CustomFieldType.String:
    default:
      return (
        <TextField
          size="small"
          fullWidth
          autoFocus={autoFocus}
          placeholder="Enter value…"
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={commitOnEnter}
        />
      );
  }
}

/**
 * Numeric input backed by a local text buffer so a mid-entry state survives its
 * own render. Emits a finite `number`, or `null` when empty or unparseable — the
 * caller disables commit on `null`, so a half-typed entry can't clear the value.
 */
function NumberValueInput({
  value,
  onChange,
  onCommit,
  autoFocus,
}: Omit<CustomFieldValueInputProps, "field">) {
  const [text, setText] = React.useState(
    typeof value === "number" ? String(value) : "",
  );

  const parsed = text === "" ? null : Number(text);
  const invalid = parsed !== null && !Number.isFinite(parsed);

  return (
    <TextField
      size="small"
      fullWidth
      type="number"
      autoFocus={autoFocus}
      placeholder="Enter a number…"
      error={invalid}
      value={text}
      onChange={(event) => {
        const raw = event.target.value;
        setText(raw);
        const next = raw === "" ? null : Number(raw);
        onChange(next !== null && Number.isFinite(next) ? next : null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit?.();
        }
      }}
    />
  );
}
